import React, { useState } from "react";
import axios from "axios";
import { API, useAuth } from "../context/AuthContext";
import { toast } from "sonner";

/**
 * "Withdraw / Manage Payouts" panel on the business dashboard.
 *
 * BookFreely does NOT hold customer funds — every booking is a Stripe Connect destination charge,
 * so the business's share lands directly in their connected Stripe account. This panel just opens
 * Stripe's Express dashboard where they can see the balance, change their bank, and trigger
 * payouts. No "internal wallet" math happens server-side.
 */
export default function WithdrawPanel({ user }) {
  const { authHeader } = useAuth();
  const [opening, setOpening] = useState(false);

  const open = async () => {
    setOpening(true);
    try {
      const r = await axios.post(`${API}/business/connect/login`, {}, { headers: authHeader() });
      window.open(r.data.url, "_blank", "noopener,noreferrer");
    } catch (e) {
      const detail = e?.response?.data?.detail || "Could not open Stripe dashboard";
      toast.error(detail);
    } finally {
      setOpening(false);
    }
  };

  const connected = !!user?.stripe_account_id;
  const ready = connected && user?.stripe_charges_enabled;

  return (
    <div data-testid="withdraw-panel" className="mt-6 rounded-2xl border p-5 flex items-start justify-between flex-wrap gap-4"
         style={{ borderColor: "var(--gf-border)", background: "var(--gf-surface)" }}>
      <div className="max-w-xl">
        <div className="text-xs uppercase tracking-[0.2em]" style={{ color: "var(--gf-text-muted)" }}>Payouts</div>
        <div className="font-display text-2xl mt-1" style={{ color: "var(--gf-text)" }}>Withdraw / Manage Payouts</div>
        <p className="text-sm mt-1" style={{ color: "var(--gf-text-muted)" }}>
          Your share of every booking is routed directly to your payout account — BookFreely never holds your money.
          Open your secure payouts dashboard to see your balance, change your bank account, and trigger payouts.
          <span className="block mt-1 text-[11px]" style={{ color: "var(--gf-text-muted)" }}>Securely powered by Stripe.</span>
        </p>
      </div>
      <button
        data-testid="open-stripe-payouts-btn"
        onClick={open}
        disabled={!ready || opening}
        className="rounded-full font-medium px-6 py-3 disabled:opacity-50"
        style={{ background: "var(--gf-accent)", color: "var(--gf-on-accent)" }}
        title={!connected ? "Complete payout setup first" : !ready ? "Payout setup not yet enabled — finish setup" : ""}
      >
        {opening ? "Opening secure dashboard…" : ready ? "Open Payouts Dashboard" : connected ? "Continue Payout Setup" : "Set Up Payouts"}
      </button>
    </div>
  );
}
